import { Fragment, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Loader2, RefreshCw, ExternalLink, ChevronDown } from 'lucide-react';
import api from '../../lib/api';

const inr = n => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const dt = v => (v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const InvoiceRow = ({ r }) => {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <tr className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${r.qualifies ? '' : 'bg-rose-50/40'}`} onClick={() => setOpen(o => !o)} data-testid={`bc-row-${r.invoice_id}`}>
        <td className="px-2 py-1.5"><a href={r.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="font-mono text-violet-700 hover:underline inline-flex items-center gap-1">{r.invoice_number}<ExternalLink className="w-3 h-3" /></a><div className="text-[10px] text-slate-400">{dt(r.date)}</div></td>
        <td className="px-2">{r.customer_name}</td>
        <td className="px-2 text-right">{inr(r.total)}</td>
        <td className="px-2 text-right">{inr(r.paid_by_deadline)}</td>
        <td className="px-2">{dt(r.last_payment)}</td>
        <td className="px-2 text-right font-semibold">{inr(r.commission)}</td>
        <td className="px-2">{r.qualifies ? <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">Counts</Badge> : <span className="text-[10px] text-rose-600">{r.reasons.join(' · ')}</span>}</td>
        <td className="px-1"><ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} /></td>
      </tr>
      {open && (
        <tr className="bg-slate-50/70"><td colSpan={8} className="px-3 py-2">
          <div className="grid md:grid-cols-3 gap-3 text-[11px]" data-testid={`bc-detail-${r.invoice_id}`}>
            <div><div className="font-semibold text-slate-700 mb-0.5">Items & commission</div>{r.lines.map((l, i) => <div key={i} className="text-slate-600">{l.name} · {l.quantity} × {inr(l.commission_each)} = <b>{inr(l.commission)}</b></div>)}</div>
            <div><div className="font-semibold text-slate-700 mb-0.5">Payments</div>{r.payments.length === 0 ? <div className="text-slate-400">No payments</div> : r.payments.map((p, i) => <div key={i} className="text-slate-600">{dt(p.date)} · <b>{inr(p.amount)}</b>{p.mode ? ` · ${p.mode}` : ''}{p.payment_number ? ` · #${p.payment_number}` : ''}</div>)}</div>
            <div><div className="font-semibold text-slate-700 mb-0.5">Credit notes / write-off</div>
              {r.credit_notes.length === 0 && !r.write_off ? <div className="text-slate-400">None</div> : <>
                {r.credit_notes.map((c, i) => <div key={i} className="text-rose-600">{c.creditnote_number} · {dt(c.date)} · {inr(c.amount)}</div>)}
                {r.write_off > 0 && <div className="text-rose-600">Write-off {inr(r.write_off)}</div>}
              </>}
              <div className="text-slate-400 mt-1">Balance today {inr(r.balance)}</div>
            </div>
          </div>
        </td></tr>
      )}
    </Fragment>
  );
};

const DetailDialog = ({ d, open, onClose }) => {
  const [show, setShow] = useState('all');
  const list = d.invoices.filter(r => show === 'all' || (show === 'ok' ? r.qualifies : !r.qualifies));
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="bc-detail-dialog">
        <DialogHeader><DialogTitle>Zoho Books commission · {d.emp_name} · {d.month}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Invoices dated in the month with salesperson {d.emp_name}. Counts when there is no credit note, no write-off and it was fully paid (within ₹{d.round_off}) by <b>{dt(d.deadline)}</b>. Commission = item Commission × quantity.</p>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge className="bg-emerald-600">{inr(d.total_commission)} from {d.qualifying} invoices</Badge>
          <Badge variant="outline" className="text-rose-600 border-rose-200">{inr(d.lost_commission)} not eligible ({d.count - d.qualifying})</Badge>
          <select value={show} onChange={e => setShow(e.target.value)} className="ml-auto h-7 border border-slate-200 rounded text-xs px-1" data-testid="bc-filter">
            <option value="all">All {d.count}</option><option value="ok">Counts</option><option value="no">Not eligible</option>
          </select>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr>
            <th className="text-left px-2 py-2">Invoice</th><th className="text-left px-2">Customer</th><th className="text-right px-2">Total</th><th className="text-right px-2">Paid by {dt(d.deadline)}</th><th className="text-left px-2">Last payment</th><th className="text-right px-2">Commission</th><th className="text-left px-2">Status</th><th />
          </tr></thead>
          <tbody>{list.map(r => <InvoiceRow key={r.invoice_id} r={r} />)}</tbody>
        </table>
        <p className="text-[10px] text-slate-400">Calculated {new Date(d.computed_at).toLocaleString('en-IN')}. Payment dates come from the app's Books payment sync.</p>
      </DialogContent>
    </Dialog>
  );
};

export const BooksCommission = ({ row, onLoaded }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = async refresh => {
    setBusy(true); setErr('');
    try {
      const r = await api.get(`/reports/incentive/${row.emp_code}/${row.month}/books-commission`, { params: { refresh } });
      setD(r.data); onLoaded && onLoaded(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not load from Zoho Books'); } finally { setBusy(false); }
  };
  useEffect(() => { load(false); }, [row.emp_code, row.month]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex items-center gap-2" data-testid="bc-field">
      <span className="text-slate-500">Total commission from Zoho Books:</span>
      {busy && !d ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> : err ? <span className="text-rose-600">{err}</span> : d && (
        <button onClick={() => setOpen(true)} className="font-bold text-emerald-700 hover:underline" data-testid="bc-open">{inr(d.total_commission)} <span className="font-normal text-slate-500">({d.qualifying}/{d.count} invoices)</span></button>
      )}
      <button onClick={() => load(true)} disabled={busy} title="Recalculate from Zoho Books" className="text-slate-400 hover:text-violet-700" data-testid="bc-refresh"><RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /></button>
      {d && <DetailDialog d={d} open={open} onClose={() => setOpen(false)} />}
    </div>
  );
};
